import React, { useEffect, useRef } from 'react';
import type { SpinnerProps, SpinnerSize } from './types';

// ─── Size class map (exported for snapshot testing) ──────────────────────────

export const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',   // 16px
  md: 'h-6 w-6',   // 24px
  lg: 'h-12 w-12', // 48px
};

// Maps the color prop (spec uses 'primary-500' naming) to Tailwind text classes.
// SVG uses currentColor, so text color drives the spinner color.
const COLOR_CLASSES: Record<string, string> = {
  'primary-500': 'text-brand-500',
  'brand-500': 'text-brand-500',
  'white': 'text-white',
  'neutral-400': 'text-neutral-400',
  'neutral-500': 'text-neutral-500',
  'success-500': 'text-success-500',
  'danger-500': 'text-danger-500',
  'warning-500': 'text-warning-500',
};

// ─── SVG spinner (internal) ───────────────────────────────────────────────────

function SpinnerSvg({ sizeClass }: { sizeClass: string }) {
  return (
    <svg
      className={['animate-spin', sizeClass].join(' ')}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({
  size = 'md',
  variant = 'inline',
  label,
  color = 'primary-500',
}: SpinnerProps) {
  const colorClass = COLOR_CLASSES[color] ?? 'text-brand-500';
  const sizeClass = SIZE_CLASSES[size];
  const ariaLabel = label ?? 'Loading';
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus trap for overlay variant
  useEffect(() => {
    if (variant !== 'overlay') return;

    const el = overlayRef.current;
    if (!el) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    el.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        e.preventDefault();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => {
      el.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [variant]);

  // ─── inline: just the spinner, fits within button/input context ──────────
  if (variant === 'inline') {
    return (
      <span
        role="status"
        aria-label={ariaLabel}
        aria-live="polite"
        className={['inline-flex items-center justify-center', colorClass].join(' ')}
      >
        <SpinnerSvg sizeClass={sizeClass} />
        <span className="sr-only">{ariaLabel}</span>
      </span>
    );
  }

  // ─── page: centered in its container (relative parent) ───────────────────
  if (variant === 'page') {
    return (
      <div
        role="status"
        aria-label={ariaLabel}
        aria-live="polite"
        className={[
          'flex flex-col items-center justify-center gap-3 w-full py-12',
          colorClass,
        ].join(' ')}
      >
        <SpinnerSvg sizeClass={sizeClass} />
        {label && (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">{label}</span>
        )}
        {!label && <span className="sr-only">{ariaLabel}</span>}
      </div>
    );
  }

  // ─── overlay: full-screen backdrop, payment processing context ───────────
  return (
    <div
      className="fixed inset-0 z-modal-backdrop flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm"
      aria-modal="true"
    >
      <div
        ref={overlayRef}
        role="status"
        aria-label={ariaLabel}
        aria-live="polite"
        tabIndex={-1}
        className={[
          'flex flex-col items-center justify-center gap-4 rounded-xl bg-white dark:bg-neutral-800',
          'px-10 py-8 shadow-xl outline-none',
          colorClass,
        ].join(' ')}
      >
        <SpinnerSvg sizeClass={sizeClass} />
        {label && (
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {label}
          </span>
        )}
        {!label && <span className="sr-only">{ariaLabel}</span>}
      </div>
    </div>
  );
}
